import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, ArrowLeft, LogOut, Shield, BookOpen, AlertTriangle, 
  Sparkles, Crown, ChevronDown, ChevronUp, Users, FileText,
  Lock, Eye, Server, CheckCircle, Info, Zap, Building2, ScrollText, BarChart3, Clock, Tag, MessageSquare, Mail, ExternalLink, Trash2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import WhatsAppActivationCard from '@/components/WhatsAppActivationCard';
import PracticeHeaderCard from '@/components/PracticeHeaderCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Simplified USD-only pricing (billed in ZAR via Paystack)
const PLAN_PRICING = {
  basic: { price: 9.99, display: '$9.99/month' },
  pro: { price: 14.99, display: '$14.99/month' },
  clinical: { price: 39.99, display: '$39.99/month' },
  extra_seat: { price: 9.99, display: '$9.99/month' }
};

// Paystack subscription links — monthly + annual per plan.
// IMPORTANT: existing subscribers on `basic` / `pro` plans continue billing
// via their already-created Paystack subscriptions; we never change THEIR
// plan codes. These links are for NEW signups only.
const DEFAULT_PAYMENT_LINKS = {
  basic: {
    monthly: 'https://paystack.shop/pay/68rz00sm-e',   // Lite Monthly
    annual:  'https://paystack.shop/pay/z60t8mwu05',   // Lite Annual (NEW Feb 2026)
  },
  pro: {
    monthly: 'https://paystack.shop/pay/y7vmt9u68h',   // PlayFirst Monthly
    annual:  'https://paystack.shop/pay/lkfcjlu1rd',   // PlayFirst Annual (NEW Feb 2026)
  },
  clinical: {
    monthly: 'https://paystack.shop/pay/ndt0dsafb0',   // Clinical Monthly
    annual:  'https://paystack.shop/pay/beh58thb3l',   // Clinical Annual (NEW Feb 2026)
  },
};

// Discount code to Paystack link mappings.
// Discount links are monthly-only for now; annual + discount combo TBD.
const DISCOUNT_CODES = {
  'FOUNDING30': {
    description: 'Lifetime 30% off',
    links: {
      basic: 'https://paystack.shop/pay/0p0cn2po35',
      pro: 'https://paystack.shop/pay/-200-fzews',
      clinical: 'https://paystack.shop/pay/onx3tcnjiv'
    }
  },
  'LAUNCH30': {
    description: '3-month special offer',
    links: {
      basic: 'https://paystack.shop/pay/h3qwpk6el-',
      pro: 'https://paystack.shop/pay/jsn719npee',
      clinical: 'https://paystack.shop/pay/lxik0x8p52'
    }
  },
  'PF-AMB-PRO': {
    description: 'Ambassador - Professional only',
    links: {
      pro: 'https://paystack.shop/pay/4wmfu9-tay'
    },
    restrictedTo: ['pro']
  },
  'PF-AMB-CLINIC': {
    description: 'Ambassador - Clinical only',
    links: {
      clinical: 'https://paystack.shop/pay/h3d4yg3ka2'
    },
    restrictedTo: ['clinical']
  }
};

// Plan definitions with USD pricing
const PLANS = {
  unlimited: {
    name: 'Unlimited',
    price: 'Unlimited',
    priceValue: 0,
    color: 'bg-purple-100 text-purple-700',
    hidden: true,  // Only visible to super admins
    features: [
      'Unlimited access to all features',
      'Unlimited children',
      'Unlimited AI activities',
      'Unlimited AI reports',
      'Admin dashboard access'
    ],
    limits: {
      children: -1,
      ai_activities: -1,
      ai_reports: -1,
      therapists: -1,
      scheduling: true
    }
  },
  super_admin: {
    name: 'Super Admin',
    price: 'Unlimited',
    priceValue: 0,
    color: 'bg-purple-100 text-purple-700',
    hidden: true,  // Only visible to super admins
    features: [
      'Unlimited access to all features',
      'Unlimited children',
      'Unlimited AI activities',
      'Unlimited AI reports',
      'Admin dashboard access'
    ],
    limits: {
      children: -1,
      ai_activities: -1,
      ai_reports: -1,
      therapists: -1,
      scheduling: true
    }
  },
  expired_trial: {
    name: 'Trial Expired',
    price: 'Upgrade Required',
    priceValue: 0,
    color: 'bg-red-100 text-red-700',
    features: [
      'Your free trial has ended',
      'Upgrade to continue using PlayFirst',
      'All features are currently locked',
      'Your data is safely preserved'
    ],
    limits: {
      children: 0,
      ai_activities: 0,
      ai_reports: 0,
      therapists: 1,
      scheduling: false
    }
  },
  free_trial: {
    name: 'Free Trial',
    price: 'Free',
    priceValue: 0,
    color: 'bg-emerald-100 text-emerald-700',
    features: [
      'Full feature access for 7 days',
      'Unlimited children (trial period)',
      'All features included',
      'AI activity generation',
      'AI report generation',
      'Scheduling & Daysheet'
    ],
    limits: {
      children: -1,
      ai_activities: -1,
      ai_reports: -1,
      therapists: 1,
      scheduling: true
    }
  },
  free: {
    name: 'Free',
    price: 'Free',
    priceValue: 0,
    color: 'bg-stone-100 text-stone-700',
    features: [
      'Up to 3 children',
      '3 AI reports/month',
      '5 AI activities/month',
      'Unlimited SOAP notes',
      'Parent portal access',
      'Scheduling & Daysheet'
    ],
    limits: {
      children: 3,
      ai_activities: 5,
      ai_reports: 3,
      therapists: 1,
      scheduling: true,
      letterhead: false
    }
  },
  basic: {
    name: 'Basic',
    price: PLAN_PRICING.basic.display,
    priceValue: PLAN_PRICING.basic.price,
    color: 'bg-stone-100 text-stone-700',
    hidden: true,  // Grandfathered — kept for legacy users only, not shown in upgrade dialog
    features: [
      'Up to 15 children',
      '15 AI reports/month',
      '25 AI activities/month',
      'Scheduling & Daysheet',
      'Custom letterhead',
      'All features included'
    ],
    limits: {
      children: 15,
      ai_activities: 25,
      ai_reports: 15,
      therapists: 1,
      scheduling: true,
      letterhead: true
    }
  },
  pro: {
    name: 'Professional',
    price: PLAN_PRICING.pro.display,
    priceValue: PLAN_PRICING.pro.price,
    color: 'bg-blue-100 text-blue-700',
    popular: true,
    features: [
      'Up to 30 children',
      '50 AI reports/month',
      '100 AI activities/month',
      'All Free features',
      'Custom letterhead',
      'WhatsApp Assistant',
      'Priority support'
    ],
    limits: {
      children: 30,
      ai_activities: 100,
      ai_reports: 50,
      therapists: 1,
      scheduling: true,
      letterhead: true
    }
  },
  clinical: {
    name: 'Clinical',
    price: PLAN_PRICING.clinical.display,
    priceValue: PLAN_PRICING.clinical.price,
    color: 'bg-purple-100 text-purple-700',
    features: [
      'Unlimited children',
      'Unlimited AI reports',
      'Unlimited AI activities',
      'Up to 5 therapists included',
      '+$9.99/extra therapist (or $89.99/yr)',
      'WhatsApp Assistant for every therapist'
    ],
    limits: {
      children: -1,
      ai_activities: -1,
      ai_reports: -1,
      therapists: 5,
      scheduling: true
    }
  }
};

// Clinical Plan Terms of Use
const CLINICAL_TERMS = `
PLAYFIRST CLINICAL LICENSE TERMS OF USE

Effective Date: Upon acceptance

1. LICENSE GRANT
This Clinical License ("License") grants the subscribing practice or organization ("Licensee") the right to use PlayFirst for up to five (5) therapists employed within the same registered practice or organization. Additional therapist seats may be purchased as per the current pricing.

2. ELIGIBILITY
The Clinical License is exclusively available to:
- Registered speech therapy practices
- Healthcare organizations employing speech-language pathologists
- Educational institutions with speech therapy departments

3. RESTRICTIONS
The Licensee agrees that:
a) This License may NOT be shared across separate practices, organizations, or legal entities
b) All therapists using this License must be employed by or contracted to the same organization
c) The License is non-transferable between organizations
d) Sublicensing is strictly prohibited

4. PRACTICE VERIFICATION
PlayFirst reserves the right to:
- Request documentation verifying practice registration
- Audit therapist employment status
- Suspend accounts found in violation of these terms

5. DATA SHARING
Under the Clinical License:
- All therapists within the practice share access to client data
- The Practice Administrator has oversight of all accounts
- Individual therapists remain responsible for their client data management

6. TERMINATION
Violation of these terms may result in:
- Immediate License termination
- Downgrade to individual Professional licenses
- Data access restrictions

7. ACKNOWLEDGMENT
By subscribing to the Clinical plan, you confirm that:
- You are authorized to enter this agreement on behalf of your practice
- All therapists added will be employed by/contracted to your organization
- You will comply with all applicable healthcare privacy regulations

For questions about eligibility, contact: michaela@playfirstapp.com
`;

const TUTORIAL_STEPS = [
  {
    title: 'Getting Started',
    content: `Welcome to PlayFirst! This app is designed to support connection-first, neurodiversity-affirming speech therapy.

**First Steps:**
1. Add your first child profile from the dashboard
2. Include their communication style (GLP, Analytic, AAC, etc.)
3. Add their interests and regulation supports`
  },
  {
    title: 'Activity Library',
    content: `The Activity Library is a shared resource for all therapists.

**Key Features:**
• Browse play-based activities by regulation level
• Use AI to generate new activities tailored to a child
• Customize any activity for specific children
• Activities include language modeling examples`
  },
  {
    title: 'Weekly Focus',
    content: `Create weekly focus plans for parents to follow at home.

**Tips:**
• Keep focus statements simple and achievable
• Include 2-3 activities maximum
• Add modeled language examples
• Use the reassurance message to support parents`
  },
  {
    title: 'Chunk Bank',
    content: `Record language samples to track a child's communication journey.

**What to Record:**
• Spontaneous language (gestalts, words, phrases)
• The context and function of each chunk
• Note the stage/level for tracking progress`
  },
  {
    title: 'Reports',
    content: `Generate professional, strengths-based clinical reports.

**Hybrid Report Generation:**
• Your entered data is automatically formatted (no AI cost)
• Add AI clinical interpretations only where needed (click per section)
• AI respects the child's Language Learner Profile

**Score Types:**
• Standard scores (mean=100, SD=15) - e.g., PLS-5, CELF-5
• Scaled scores (mean=10, SD=3) - e.g., CELF subtests
• Select the correct type when entering scores for accurate interpretation

**Report Sections:**
• Header & Case History
• Receptive & Expressive Language  
• Literacy & Phonological Awareness
• Speech & Motor
• Summary & Recommendations

**Tip:** Always review AI-generated content before sharing. The AI provides clinical support, not diagnostic conclusions.`
  },
  {
    title: 'Assessments',
    content: `Track formal and informal assessments for each child.

**Features:**
• 25+ standardized tests pre-configured (PLS-5, CELF-5, GFTA-3, etc.)
• Non-standardized measures: language samples, observations
• Literacy and Auditory Processing assessments (CTOPP, TAPS, etc.)
• Auto-import language samples from the Chunk Bank

**Language Learner Profile:**
When creating an assessment, select the child's language learning style (GLP, Analytical, Mixed, AAC, or Other). This guides AI interpretations to be strengths-based and neurodiversity-affirming.`
  },
  {
    title: 'Generating Reports',
    content: `Follow these steps to generate a clinical report:

**Step 1: Enter Assessment Data**
• Go to the child's profile → Assessments
• Click "New Assessment" or edit an existing one
• Select the Language Learner Profile (GLP, Analytical, etc.)
• Fill in relevant sections (scores, observations, clinical impressions)
• Click "Save Assessment"

**Step 2: Open the Report Builder**
• In the assessment list, find your saved assessment
• Click the 👁 (eye icon) to view the assessment
• Click "Build Report (Recommended)"

**Step 3: Generate AI Interpretations**
• Your entered data appears auto-formatted in each section
• Click "Add Clinical Interpretation" on any section to add AI analysis
• The AI uses your Language Learner Profile to create affirming interpretations
• Review and edit the AI text as needed

**Step 4: Copy or Print**
• Use "Copy Section" buttons to copy individual sections
• Use "Copy All" to copy the entire report
• Print directly from the browser for a clean format`
  },
  {
    title: 'Session Notes',
    content: `Document therapy sessions using SOAP note format.

**What's Included:**
• Subjective - Parent/caregiver reports, child's mood
• Objective - Activities, data collected, observations
• Assessment - Clinical interpretation, progress toward goals
• Plan - Next steps, homework, recommendations

**AI Parent Summary:**
• Click "Generate AI Summary" to create a parent-friendly summary
• The AI writes a warm message with session highlights and home tips
• Toggle "Share with parent" to send to the parent portal

**Auto-Daysheet:**
• Set the session time when saving a SOAP note
• The entry automatically appears on your daysheet/timetable`
  },
  {
    title: 'Daysheet & Timetable',
    content: `Track your weekly schedule with the Daysheet feature.

**Two View Modes:**
• **List View** - See all clients for each day at a glance
• **Timetable View** - Visual schedule grid showing time slots

**Key Features:**
• Auto-populates when you save a SOAP note with a time
• Drag and drop to reschedule clients between days
• Add treatment codes (CPT) and notes for billing
• Export to PDF or print for your records

**Getting Started:**
1. Go to "Daysheet" from the main menu
2. Add entries manually or save SOAP notes with times
3. Use timetable view to see your full schedule
4. Drag entries to reschedule as needed

**Note:** Scheduling is included with paid plans (Basic, Professional, Clinical).`
  },
  {
    title: 'Parent Portal',
    content: `Parents access their own portal via an invite code you generate.

**How Parents Join:**
1. Open the child's profile → click "Invite Parent"
2. Share the unique 6-character code with the parent
3. Parent creates an account using the code
4. They now see their child's shared content

**What Parents See in Their Portal:**
• **Weekly Focus** - Current therapy goals and home activities
• **Activity Library** - Resources and activities you've shared
• **Progress Updates** - Session notes marked "Share with parent"
• **Notifications** - Updates when you post new content

**What Parents Can Do:**
• Mark activities as completed
• Add notes/comments on activities  
• View their child's interests and strengths
• Access educational resources

**Privacy Protection:**
• Parents only see their own child
• Clinical reports are NOT visible unless you explicitly share
• Detailed assessment data stays private
• SOAP notes are only visible if you toggle "Share"

**Tip:** The Weekly Focus is the heart of the parent experience - keep it updated each week!`
  },
  {
    title: 'Tips & Best Practices',
    content: `Get the most out of PlayFirst with these tips:

**For Efficiency:**
• Use the AI Activity Generator to create personalized activities quickly
• Save assessment templates with your commonly used tests
• Set up your letterhead once in Settings → it appears on all reports

**For Parents:**
• Update Weekly Focus every week - parents check it regularly
• Use the "Generate AI Summary" on SOAP notes for parent-friendly updates
• Share resources from the Activity Library that match home goals

**For Reports:**
• Enter data first, then add AI interpretations section-by-section
• Review AI content carefully before finalizing
• Copy sections to Word for letterhead formatting

**Neurodiversity-Affirming Practice:**
• PlayFirst uses strengths-based language throughout
• GLP learners: track NLA stages 1-6 accurately
• AAC users: "multimodal communicator" terminology
• Focus on what children CAN do, not deficits`
  }
];

export default function TherapistSettings({ onLogout }) {
  const navigate = useNavigate();
  const location = window.location;
  const searchParams = new URLSearchParams(location.search);
  const sectionParam = searchParams.get('section');
  
  const [expandedSections, setExpandedSections] = useState({
    privacy: false,
    tutorial: sectionParam === 'tutorial' ? true : true,
    dataMinimization: false,
    aiLimits: false
  });
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showClinicalTerms, setShowClinicalTerms] = useState(false);
  const [clinicalTermsAccepted, setClinicalTermsAccepted] = useState(false);
  const [currentTutorialStep, setCurrentTutorialStep] = useState(0);
  
  // Auto-scroll to tutorial section if coming from help button
  useEffect(() => {
    if (sectionParam === 'tutorial') {
      setTimeout(() => {
        const tutorialSection = document.querySelector('[data-testid="tutorial-section"]');
        if (tutorialSection) {
          tutorialSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [sectionParam]);
  
  const [usage, setUsage] = useState({
    children: 0,
    ai_activities_used: 0,
    ai_reports_used: 0,
    therapists: 1
  });
  const [analytics, setAnalytics] = useState(null);
  const [currentPlan, setCurrentPlan] = useState('basic');
  const [practiceInfo, setPracticeInfo] = useState(null);
  // Join Practice state
  const [showJoinPracticeDialog, setShowJoinPracticeDialog] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joiningPractice, setJoiningPractice] = useState(false);
  const [showManageSubscription, setShowManageSubscription] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    fetchUsage();
    fetchAnalytics();
    fetchSubscriptionDetails();
  }, []);

  const fetchSubscriptionDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/billing/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm(
      "Cancel your PlayFirst subscription?\n\n" +
      "• Your subscription will NOT auto-renew.\n" +
      "• You keep full access until the end of your current billing period.\n" +
      "• After that your account becomes inactive — no further charges.\n" +
      "• You can re-subscribe any time and pick up where you left off.\n\n" +
      "Proceed with cancellation?"
    )) {
      return;
    }
    
    setCancellingSubscription(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/billing/cancel-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setShowManageSubscription(false);
      fetchSubscriptionDetails();
      fetchUsage();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/ai-usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const [trialStatus, setTrialStatus] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [discountError, setDiscountError] = useState('');
  const [discountValid, setDiscountValid] = useState(false);
  // Server-side-validated promo codes (e.g. EXPO30 with a hard timezone-
  // aware expiry). Populated by validateDiscountCode(); read by
  // getPaymentLink(). Keyed by uppercased code → {plan → redirect_url}.
  const [serverPromo, setServerPromo] = useState(null);  // {code, links: {pro, clinical}}
  // Monthly vs annual billing toggle in the upgrade dialog. Annual = ~25% off.
  const [upgradeBillingPeriod, setUpgradeBillingPeriod] = useState('monthly');

  const fetchUsage = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Get subscription data from backend (includes real AI usage)
      const subscriptionRes = await axios.get(`${API}/billing/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = subscriptionRes.data;
      
      // Simulated practice info for Clinical plan
      const savedPractice = JSON.parse(localStorage.getItem('practice_info') || 'null');
      if (savedPractice) {
        setPracticeInfo(savedPractice);
      }
      
      setUsage({
        children: data.usage?.children || 0,
        ai_activities_used: data.usage?.ai_activities || 0,
        ai_reports_used: data.usage?.ai_reports || 0,
        therapists: savedPractice?.therapists?.length || 1
      });
      
      setCurrentPlan(data.effective_plan || data.plan || 'free_trial');
      setTrialStatus(data.trial || null);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
      // Fallback to basic counts
      try {
        const token = localStorage.getItem('token');
        const childrenRes = await axios.get(`${API}/children`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsage(prev => ({ ...prev, children: childrenRes.data.length }));
      } catch (e) {
        console.error('Fallback also failed');
      }
    }
  };

  // Get payment link for a plan based on discount code + billing period.
  // Billing period defaults to 'monthly'. Discount codes are monthly-only
  // for now — if the user has a discount code applied and picks annual we
  // ignore the discount (the toast in initiateCheckout warns them).
  const getPaymentLink = (planKey, period = 'monthly') => {
    const code = discountCode.trim().toUpperCase();

    // No code or annual selection → use default links (monthly or annual)
    if (!code || period === 'annual') {
      const entry = DEFAULT_PAYMENT_LINKS[planKey];
      if (!entry) return null;
      return entry[period] || entry.monthly;
    }

    // Server-validated promo (e.g. EXPO30) — takes precedence over the
    // client-side DISCOUNT_CODES map because its expiry is enforced
    // server-side and we can't trust the client clock.
    if (serverPromo && serverPromo.code === code) {
      const serverLink = (serverPromo.links || {})[planKey];
      if (serverLink) return serverLink;
      // Server code has no link for this plan — treat as restricted.
      return 'restricted';
    }

    // Check if code exists in the client-side map (existing behaviour)
    const discountConfig = DISCOUNT_CODES[code];
    if (!discountConfig) {
      return null; // Invalid code
    }

    // Check if code is restricted to specific plans
    if (discountConfig.restrictedTo && !discountConfig.restrictedTo.includes(planKey)) {
      return 'restricted'; // Code not valid for this plan
    }

    // Check if code has a link for this plan
    if (!discountConfig.links[planKey]) {
      return null; // Code doesn't support this plan
    }

    return discountConfig.links[planKey];
  };

  // Validate discount code. First checks the client-side DISCOUNT_CODES
  // map (existing behaviour, unchanged). If not found there, falls back to
  // the server-side validator that enforces expiry in Africa/Johannesburg
  // and returns plan-specific redirect URLs (used by EXPO30). Codes that
  // have already expired server-side receive the exact expiry message the
  // partner_codes doc carries — client clock is never trusted.
  const validateDiscountCode = async () => {
    const code = discountCode.trim().toUpperCase();
    setDiscountError('');
    setDiscountValid(false);
    setServerPromo(null);

    if (!code) {
      return;
    }

    // Client-side codes first (existing).
    const discountConfig = DISCOUNT_CODES[code];
    if (discountConfig) {
      setDiscountValid(true);
      toast.success(`Discount code "${code}" applied! ${discountConfig.description}`);
      return;
    }

    // Not in client map → hit the server-side validator. Try both plans
    // so we know which links this code supports before the user has to
    // pick one. We stash them and let getPaymentLink() route on click.
    try {
      const [proResp, clinicalResp] = await Promise.all([
        axios.post(`${API}/billing/validate-promo-code-with-plan`, { code, plan: 'pro' }),
        axios.post(`${API}/billing/validate-promo-code-with-plan`, { code, plan: 'clinical' }),
      ]);
      const links = {};
      if (proResp.data?.valid) links.pro = proResp.data.redirect_url;
      if (clinicalResp.data?.valid) links.clinical = clinicalResp.data.redirect_url;

      if (Object.keys(links).length === 0) {
        // Neither plan validated. Surface the server's own message — this
        // is the "expo has ended" copy when EXPO30 is past its expiry.
        const msg = proResp.data?.message
          || clinicalResp.data?.message
          || 'This promotional code is not valid.';
        setDiscountError(msg);
        return;
      }

      setServerPromo({ code, links });
      setDiscountValid(true);
      const successMsg = (proResp.data?.valid && proResp.data.message)
        || (clinicalResp.data?.valid && clinicalResp.data.message)
        || `${code} applied! You'll receive 30% off when you continue to checkout.`;
      toast.success(successMsg);
    } catch (err) {
      setDiscountError('This promotional code is not valid.');
    }
  };

  const handlePlanSelect = async (planKey) => {
    if (planKey === 'free') {
      // Activate free plan via API (no payment needed)
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/user/activate-free-plan`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Free plan activated! You now have access to basic features.');
        setCurrentPlan('free');
        setShowUpgradeDialog(false);
        window.location.reload();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to activate free plan');
      }
    } else if (planKey === 'clinical') {
      // Show terms for Clinical plan
      setClinicalTermsAccepted(false);
      setShowClinicalTerms(true);
    } else {
      // Initiate checkout for Basic or Pro
      await initiateCheckout(planKey);
    }
  };

  // Guard against double-click: while a Paystack redirect is being
  // triggered we short-circuit further calls so the promo can't be
  // applied twice or fire duplicate window.location.href navigations.
  const checkoutInFlightRef = useRef(false);

  const initiateCheckout = async (planKey) => {
    if (checkoutInFlightRef.current) return;
    const period = upgradeBillingPeriod || 'monthly';
    const link = getPaymentLink(planKey, period);

    if (link === null) {
      toast.error('This promotional code is not valid.');
      return;
    }

    if (link === 'restricted') {
      const upper = discountCode.trim().toUpperCase();
      const clientCfg = DISCOUNT_CODES[upper];
      if (clientCfg?.restrictedTo) {
        const validPlans = clientCfg.restrictedTo.map(p => PLANS[p]?.name).filter(Boolean).join(' or ');
        toast.error(`This code is only valid for ${validPlans} plan`);
      } else if (serverPromo && serverPromo.code === upper) {
        const validPlans = Object.keys(serverPromo.links || {})
          .map(p => PLANS[p]?.name)
          .filter(Boolean)
          .join(' or ');
        toast.error(`This code is only valid for ${validPlans} plan`);
      } else {
        toast.error('This code is not valid for this plan');
      }
      return;
    }

    if (discountCode.trim() && period === 'annual') {
      toast.info('Discount codes are monthly-only. Proceeding with annual pricing (~25% off vs monthly).');
    }

    // Redirect to Paystack subscription link
    checkoutInFlightRef.current = true;
    window.location.href = link;
  };

  const handleClinicalTermsAccept = async () => {
    if (!clinicalTermsAccepted) {
      toast.error('Please accept the terms to continue');
      return;
    }
    setShowClinicalTerms(false);
    await initiateCheckout('clinical');
  };

  const handleJoinPractice = async () => {
    if (!joinCode.trim()) {
      toast.error('Please enter an invite code');
      return;
    }
    
    setJoiningPractice(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/practices/join`,
        { invite_code: joinCode.trim().toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || 'Successfully joined practice!');
      setShowJoinPracticeDialog(false);
      setJoinCode('');
      
      // Refresh user data to get updated plan
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      localStorage.setItem('user', JSON.stringify(userResponse.data));
      
      // Redirect to practice admin
      navigate('/practice');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join practice. Please check your code.');
    } finally {
      setJoiningPractice(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const currentPlanData = PLANS[currentPlan] || PLANS.free_trial;
  const limits = currentPlanData?.limits || PLANS.free_trial.limits;

  const getUsagePercent = (used, limit) => {
    if (limit === -1) return 0; // unlimited
    if (limit === 0) return 100; // not available
    return Math.min((used / limit) * 100, 100);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              data-testid="back-btn"
              onClick={() => navigate('/')}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-2xl font-serif text-primary-900">PlayFirst</h1>
            </div>
          </div>
          <Button variant="outline" data-testid="logout-btn" onClick={onLogout} className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-8">
            <h2 className="text-4xl md:text-5xl font-serif font-normal text-primary-900 mb-2">
              Settings & Info
            </h2>
            <p className="text-lg text-stone-600">Important information for therapists</p>
          </div>

          {/* Current Plan Card */}
          <Card className="p-6 bg-gradient-to-br from-primary/5 to-primary/10 rounded-3xl border-0 mb-8" data-testid="current-plan-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Current Plan</p>
                  <p className="text-2xl font-serif text-primary-900">{currentPlanData?.name || 'Free Trial'}</p>
                </div>
              </div>
              <Button 
                onClick={() => setShowUpgradeDialog(true)}
                className="rounded-full"
                data-testid="upgrade-btn"
              >
                <Zap className="w-4 h-4 mr-2" />
                {['basic', 'pro', 'clinical'].includes(currentPlan) ? 'Change Plan' : 'Upgrade'}
              </Button>
              {['basic', 'pro', 'clinical'].includes(currentPlan) && subscriptionDetails?.subscription_status === 'active' && (
                <Button 
                  variant="outline"
                  onClick={() => setShowManageSubscription(true)}
                  className="rounded-full ml-2 border-red-200 text-red-600 hover:bg-red-50"
                  data-testid="manage-subscription-btn"
                >
                  Cancel
                </Button>
              )}
            </div>

            {/* Trial Status Banner */}
            {trialStatus && currentPlan === 'free_trial' && (
              <div className={`mb-4 p-4 rounded-2xl border ${
                trialStatus.expired 
                  ? 'bg-red-50 border-red-200' 
                  : trialStatus.days_remaining <= 2 
                    ? 'bg-amber-50 border-amber-200' 
                    : 'bg-emerald-50 border-emerald-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      trialStatus.expired 
                        ? 'bg-red-100' 
                        : trialStatus.days_remaining <= 2 
                          ? 'bg-amber-100' 
                          : 'bg-emerald-100'
                    }`}>
                      <Clock className={`w-5 h-5 ${
                        trialStatus.expired 
                          ? 'text-red-600' 
                          : trialStatus.days_remaining <= 2 
                            ? 'text-amber-600' 
                            : 'text-emerald-600'
                      }`} />
                    </div>
                    <div>
                      {trialStatus.expired ? (
                        <>
                          <p className="font-medium text-red-900">Trial Expired</p>
                          <p className="text-sm text-red-600">Activate the free plan or subscribe for full access</p>
                        </>
                      ) : (
                        <>
                          <p className="font-medium text-emerald-900">
                            {trialStatus.days_remaining} day{trialStatus.days_remaining !== 1 ? 's' : ''} remaining
                          </p>
                          <p className="text-sm text-emerald-600">
                            7-day free trial • Full feature access
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  {trialStatus.expired && (
                    <div className="flex gap-2">
                      <Button 
                        variant="outline"
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            await axios.post(`${API}/user/activate-free-plan`, {}, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success('Free plan activated! You now have access to basic features.');
                            setCurrentPlan('free');
                            window.location.reload();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to activate free plan');
                          }
                        }}
                        className="rounded-full"
                        data-testid="activate-free-btn"
                      >
                        Use Free Plan
                      </Button>
                      <Button 
                        onClick={() => setShowUpgradeDialog(true)}
                        className="rounded-full bg-red-600 hover:bg-red-700"
                      >
                        Subscribe
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Clinical Plan - Practice Admin Link */}
            {currentPlan === 'clinical' && (
              <div className="mt-4 p-4 bg-purple-50 rounded-2xl border border-purple-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="font-medium text-purple-900">Practice Admin Dashboard</p>
                      <p className="text-sm text-purple-600">Manage team members and view practice-wide stats</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="rounded-full border-purple-300 text-purple-700 hover:bg-purple-100"
                    onClick={() => navigate('/practice')}
                    data-testid="practice-admin-btn"
                  >
                    Open Dashboard
                  </Button>
                </div>
              </div>
            )}

            {/* Join Practice Option - for therapists not on clinical plan */}
            {currentPlan !== 'clinical' && (
              <div className="mt-4 p-4 bg-indigo-50 rounded-2xl border border-indigo-200">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-indigo-600" />
                    <div>
                      <p className="font-medium text-indigo-900">Join a Practice</p>
                      <p className="text-sm text-indigo-600">Have an invite code from your practice admin?</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="rounded-full border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                    onClick={() => setShowJoinPracticeDialog(true)}
                    data-testid="join-practice-btn"
                  >
                    Enter Code
                  </Button>
                </div>
              </div>
            )}

            {/* Usage Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="bg-white/60 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-stone-600">Children</span>
                  <Users className="w-4 h-4 text-stone-400" />
                </div>
                <p className="text-2xl font-bold text-primary-900">
                  {usage.children} / {limits.children === -1 ? '∞' : limits.children}
                </p>
                <Progress value={getUsagePercent(usage.children, limits.children)} className="h-2 mt-2" />
              </div>
              
              <div className="bg-white/60 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-stone-600">AI Activities</span>
                  <Sparkles className="w-4 h-4 text-stone-400" />
                </div>
                <p className="text-2xl font-bold text-primary-900">
                  {usage.ai_activities_used} / {limits.ai_activities}
                </p>
                <Progress value={getUsagePercent(usage.ai_activities_used, limits.ai_activities)} className="h-2 mt-2" />
              </div>
              
              <div className="bg-white/60 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-stone-600">AI Reports (incl. Assessments)</span>
                  <FileText className="w-4 h-4 text-stone-400" />
                </div>
                <p className="text-2xl font-bold text-primary-900">
                  {limits.ai_reports === 0 ? 'Not included' : `${usage.ai_reports_used} / ${limits.ai_reports}`}
                </p>
                {limits.ai_reports > 0 && (
                  <Progress value={getUsagePercent(usage.ai_reports_used, limits.ai_reports)} className="h-2 mt-2" />
                )}
              </div>
            </div>
          </Card>

          {/* AI Usage Analytics Card */}
          {analytics && (
            <Card className="p-6 bg-white rounded-3xl border border-stone-100 mb-8" data-testid="analytics-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-primary-900">AI Usage Analytics</h3>
                  <p className="text-sm text-stone-500">{analytics.current_month?.month} • {analytics.current_month?.days_remaining} days remaining</p>
                </div>
              </div>

              {/* Current Month Progress */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-800">AI Activities</span>
                    <span className="text-xs text-blue-600 bg-blue-200 px-2 py-0.5 rounded-full">This month</span>
                  </div>
                  <p className="text-3xl font-bold text-blue-900">
                    {analytics.current_month?.activities || 0}
                    <span className="text-lg font-normal text-blue-600">/{analytics.limits?.ai_activities}</span>
                  </p>
                  <Progress 
                    value={analytics.limits?.ai_activities > 0 
                      ? (analytics.current_month?.activities / analytics.limits?.ai_activities) * 100 
                      : 0} 
                    className="h-2 mt-2" 
                  />
                </div>
                
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-purple-800">AI Reports</span>
                    <span className="text-xs text-purple-600 bg-purple-200 px-2 py-0.5 rounded-full">This month</span>
                  </div>
                  <p className="text-3xl font-bold text-purple-900">
                    {analytics.limits?.ai_reports === 0 
                      ? <span className="text-lg">Not included</span>
                      : <>
                          {analytics.current_month?.reports || 0}
                          <span className="text-lg font-normal text-purple-600">/{analytics.limits?.ai_reports}</span>
                        </>
                    }
                  </p>
                  {analytics.limits?.ai_reports > 0 && (
                    <Progress 
                      value={(analytics.current_month?.reports / analytics.limits?.ai_reports) * 100} 
                      className="h-2 mt-2" 
                    />
                  )}
                </div>
              </div>

              {/* Monthly History Chart (Simple bar visualization) */}
              {analytics.monthly_history && analytics.monthly_history.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-stone-700 mb-3">Usage History (Last 6 Months)</h4>
                  <div className="flex items-end gap-2 h-24">
                    {analytics.monthly_history.map((month, i) => {
                      const maxVal = Math.max(...analytics.monthly_history.map(m => m.activities + m.reports), 1);
                      const height = ((month.activities + month.reports) / maxVal) * 100;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                          <div 
                            className="w-full bg-gradient-to-t from-primary/60 to-primary/40 rounded-t-lg transition-all"
                            style={{ height: `${Math.max(height, 4)}%` }}
                            title={`${month.activities} activities, ${month.reports} reports`}
                          />
                          <span className="text-xs text-stone-500">{month.month.split(' ')[0]}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* All-time Stats */}
              <div className="bg-stone-50 rounded-2xl p-4">
                <h4 className="text-sm font-medium text-stone-700 mb-3">All-Time Stats</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-primary-900">{analytics.all_time?.activities || 0}</p>
                    <p className="text-xs text-stone-500">Activities Generated</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary-900">{analytics.all_time?.reports || 0}</p>
                    <p className="text-xs text-stone-500">Reports Generated</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary-900">
                      {((analytics.all_time?.total_input_tokens || 0) + (analytics.all_time?.total_output_tokens || 0)).toLocaleString()}
                    </p>
                    <p className="text-xs text-stone-500">Total Tokens</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">${(analytics.all_time?.estimated_cost_usd || 0).toFixed(2)}</p>
                    <p className="text-xs text-stone-500">Est. AI Cost</p>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Quick Actions */}
          <Card className="p-6 bg-white rounded-3xl border border-stone-100 mb-8" data-testid="quick-actions-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Eye className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-primary-900">Quick Actions</h3>
                <p className="text-sm text-stone-500">Manage shared content with parents</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={() => window.location.href = '/shared-notes'}
                className="h-auto py-4 px-4 rounded-xl justify-start"
                data-testid="shared-notes-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-primary-900">Shared Notes Summary</p>
                    <p className="text-xs text-stone-500">View all notes shared with parents</p>
                  </div>
                </div>
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location.href = '/resources'}
                className="h-auto py-4 px-4 rounded-xl justify-start"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-primary-900">Resource Library</p>
                    <p className="text-xs text-stone-500">Educational resources to share</p>
                  </div>
                </div>
              </Button>
            </div>
          </Card>

          {/* WhatsApp Bot Activation */}
          <WhatsAppActivationCard />

          {/* Practice Header for invoices */}
          <PracticeHeaderCard />

          {/* Expandable Sections */}
          <div className="space-y-4">

            {/* Data Minimization Warning */}
            <Card 
              className="rounded-3xl border border-amber-200 bg-amber-50 overflow-hidden"
              data-testid="data-minimization-section"
            >
              <button
                onClick={() => toggleSection('dataMinimization')}
                className="w-full p-6 flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-amber-900">Data Minimization</h3>
                    <p className="text-sm text-amber-700">Important privacy reminder</p>
                  </div>
                </div>
                {expandedSections.dataMinimization ? (
                  <ChevronUp className="w-5 h-5 text-amber-600" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-amber-600" />
                )}
              </button>
              
              {expandedSections.dataMinimization && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  className="px-6 pb-6"
                >
                  <div className="bg-white rounded-2xl p-4 border border-amber-200">
                    <h4 className="font-medium text-amber-900 mb-3">Best Practices for Client Privacy</h4>
                    <ul className="space-y-2 text-amber-800">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <span><strong>Use first names only</strong> - Never enter a child's full name or surname</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <span><strong>Avoid identifying details</strong> - Don't include addresses, school names, or other identifiable information</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <span><strong>Be mindful of notes</strong> - Progress notes and observations should focus on therapy, not personal details</span>
                      </li>
                    </ul>
                    <div className="mt-4 p-3 bg-amber-50 rounded-xl">
                      <p className="text-sm text-amber-700">
                        <Info className="w-4 h-4 inline mr-1" />
                        Remember: The less identifying information stored, the better protected your clients are.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </Card>

            {/* App Tutorial */}
            <Card 
              className="rounded-3xl border border-stone-100 bg-white overflow-hidden"
              data-testid="tutorial-section"
            >
              <button
                onClick={() => toggleSection('tutorial')}
                className="w-full p-6 flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-primary-900">App Tutorial</h3>
                    <p className="text-sm text-stone-500">Learn how to use PlayFirst</p>
                  </div>
                </div>
                {expandedSections.tutorial ? (
                  <ChevronUp className="w-5 h-5 text-stone-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-stone-400" />
                )}
              </button>
              
              {expandedSections.tutorial && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  className="px-6 pb-6"
                >
                  {/* Tutorial Step Indicators */}
                  <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                    {TUTORIAL_STEPS.map((step, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentTutorialStep(index)}
                        className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                          currentTutorialStep === index
                            ? 'bg-primary text-white'
                            : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                        }`}
                      >
                        {step.title}
                      </button>
                    ))}
                  </div>
                  
                  {/* Tutorial Content */}
                  <div className="bg-stone-50 rounded-2xl p-6">
                    <h4 className="text-xl font-serif text-primary-900 mb-4">
                      {TUTORIAL_STEPS[currentTutorialStep].title}
                    </h4>
                    <div className="prose prose-stone max-w-none">
                      <div className="whitespace-pre-wrap text-stone-700 leading-relaxed">
                        {TUTORIAL_STEPS[currentTutorialStep].content}
                      </div>
                    </div>
                    
                    {/* Navigation */}
                    <div className="flex justify-between mt-6">
                      <Button
                        variant="outline"
                        className="rounded-full"
                        disabled={currentTutorialStep === 0}
                        onClick={() => setCurrentTutorialStep(prev => prev - 1)}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-stone-400 self-center">
                        {currentTutorialStep + 1} of {TUTORIAL_STEPS.length}
                      </span>
                      <Button
                        variant="outline"
                        className="rounded-full"
                        disabled={currentTutorialStep === TUTORIAL_STEPS.length - 1}
                        onClick={() => setCurrentTutorialStep(prev => prev + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
            </Card>

            {/* AI Limits Info */}
            <Card 
              className="rounded-3xl border border-stone-100 bg-white overflow-hidden"
              data-testid="ai-limits-section"
            >
              <button
                onClick={() => toggleSection('aiLimits')}
                className="w-full p-6 flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-primary-900">AI Generation Limits</h3>
                    <p className="text-sm text-stone-500">Understanding your plan's AI capabilities</p>
                  </div>
                </div>
                {expandedSections.aiLimits ? (
                  <ChevronUp className="w-5 h-5 text-stone-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-stone-400" />
                )}
              </button>
              
              {expandedSections.aiLimits && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  className="px-6 pb-6"
                >
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-stone-200">
                          <th className="text-left py-3 px-4 font-medium text-stone-500">Feature</th>
                          <th className="text-center py-3 px-4 font-medium text-stone-500">Basic</th>
                          <th className="text-center py-3 px-4 font-medium text-blue-600">Pro</th>
                          <th className="text-center py-3 px-4 font-medium text-purple-600">Clinical</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-stone-100">
                          <td className="py-3 px-4 text-stone-700">Children</td>
                          <td className="py-3 px-4 text-center">10</td>
                          <td className="py-3 px-4 text-center">20</td>
                          <td className="py-3 px-4 text-center">Unlimited</td>
                        </tr>
                        <tr className="border-b border-stone-100">
                          <td className="py-3 px-4 text-stone-700">AI Activities / month</td>
                          <td className="py-3 px-4 text-center">20</td>
                          <td className="py-3 px-4 text-center">50</td>
                          <td className="py-3 px-4 text-center">150</td>
                        </tr>
                        <tr className="border-b border-stone-100">
                          <td className="py-3 px-4 text-stone-700">AI Reports / month<br/><span className="text-xs text-stone-400">(Progress & Assessment Reports)</span></td>
                          <td className="py-3 px-4 text-center text-stone-400">—</td>
                          <td className="py-3 px-4 text-center">10</td>
                          <td className="py-3 px-4 text-center">50</td>
                        </tr>
                        <tr className="border-b border-stone-100">
                          <td className="py-3 px-4 text-stone-700">Educational Resources</td>
                          <td className="py-3 px-4 text-center"><CheckCircle className="w-4 h-4 mx-auto text-green-500" /></td>
                          <td className="py-3 px-4 text-center"><CheckCircle className="w-4 h-4 mx-auto text-green-500" /></td>
                          <td className="py-3 px-4 text-center"><CheckCircle className="w-4 h-4 mx-auto text-green-500" /></td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 text-stone-700">Priority Support</td>
                          <td className="py-3 px-4 text-center text-stone-400">—</td>
                          <td className="py-3 px-4 text-center"><CheckCircle className="w-4 h-4 mx-auto text-green-500" /></td>
                          <td className="py-3 px-4 text-center"><CheckCircle className="w-4 h-4 mx-auto text-green-500" /></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-sm text-stone-500 mt-4">
                    <Info className="w-4 h-4 inline mr-1" />
                    Limits reset on the 1st of each month. Unused generations don't roll over.
                  </p>
                </motion.div>
              )}
            </Card>

            {/* Privacy Policy */}
            <Card 
              className="rounded-3xl border border-stone-100 bg-white overflow-hidden"
              data-testid="privacy-section"
            >
              <button
                onClick={() => toggleSection('privacy')}
                className="w-full p-6 flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-primary-900">Privacy & Security</h3>
                    <p className="text-sm text-stone-500">How we protect your data</p>
                  </div>
                </div>
                {expandedSections.privacy ? (
                  <ChevronUp className="w-5 h-5 text-stone-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-stone-400" />
                )}
              </button>
              
              {expandedSections.privacy && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  className="px-6 pb-6"
                >
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-4 bg-stone-50 rounded-2xl">
                      <Lock className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-primary-900">Data Encryption</h4>
                        <p className="text-sm text-stone-600">All data is encrypted in transit using TLS/SSL</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-4 bg-stone-50 rounded-2xl">
                      <Server className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-primary-900">Secure Storage</h4>
                        <p className="text-sm text-stone-600">Data stored on secure cloud infrastructure</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-4 bg-stone-50 rounded-2xl">
                      <Eye className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-primary-900">No Data Selling</h4>
                        <p className="text-sm text-stone-600">We never sell or share your client data with third parties</p>
                      </div>
                    </div>

                    <div className="mt-6 p-4 bg-primary/5 rounded-2xl">
                      <p className="text-sm text-stone-600 mb-3">
                        For our full privacy policy and terms of service:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          variant="outline" 
                          className="rounded-full text-sm" 
                          size="sm"
                          onClick={() => window.location.href = '/hipaa'}
                          data-testid="privacy-policy-btn"
                        >
                          Privacy Policy
                        </Button>
                        <Button 
                          variant="outline" 
                          className="rounded-full text-sm" 
                          size="sm"
                          onClick={() => window.location.href = '/terms'}
                        >
                          Terms of Service
                        </Button>
                        <Button 
                          variant="outline" 
                          className="rounded-full text-sm" 
                          size="sm"
                          onClick={() => navigate('/hipaa')}
                          data-testid="hipaa-compliance-btn"
                        >
                          HIPAA Compliance
                        </Button>
                        <Button 
                          variant="outline" 
                          className="rounded-full text-sm" 
                          size="sm"
                          onClick={() => navigate('/refund-policy')}
                          data-testid="refund-policy-btn"
                        >
                          Refund Policy
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </Card>

            {/* Data & Privacy */}
            <Card className="p-6 bg-white rounded-3xl border border-stone-100" data-testid="data-privacy-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-medium text-primary-900">Data & Privacy</h3>
              </div>
              <div className="space-y-3">
                <a href="/privacy" className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors">
                  <Shield className="w-4 h-4 text-stone-500" />
                  <span className="text-sm text-stone-700">View Privacy Policy (POPIA, Australian Privacy Act, HIPAA)</span>
                </a>
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const res = await axios.get(`${API}/user/export-data`, { headers: { Authorization: `Bearer ${token}` } });
                      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `playfirst-data-export-${new Date().toISOString().split('T')[0]}.json`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success('Data exported successfully');
                    } catch { toast.error('Failed to export data'); }
                  }}
                  className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors w-full text-left"
                  data-testid="export-data-btn"
                >
                  <ExternalLink className="w-4 h-4 text-stone-500" />
                  <span className="text-sm text-stone-700">Export All My Data</span>
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to permanently delete your account and ALL associated data? This action cannot be undone.')) {
                      if (window.confirm('This will delete all child profiles, session notes, assessments, activities, and your account. Type DELETE to confirm.')) {
                        const confirmation = window.prompt('Type DELETE to confirm permanent deletion:');
                        if (confirmation === 'DELETE') {
                          const token = localStorage.getItem('token');
                          axios.delete(`${API}/user/delete-account`, { headers: { Authorization: `Bearer ${token}` } })
                            .then(() => {
                              localStorage.clear();
                              toast.success('Account deleted');
                              window.location.href = '/';
                            })
                            .catch(() => toast.error('Failed to delete account'));
                        }
                      }
                    }
                  }}
                  className="flex items-center gap-3 p-3 bg-red-50 rounded-xl hover:bg-red-100 transition-colors w-full text-left"
                  data-testid="delete-account-btn"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                  <span className="text-sm text-red-700">Delete My Account & All Data</span>
                </button>
              </div>
            </Card>

            {/* Contact & Support */}
            <Card className="p-6 bg-white rounded-3xl border border-stone-100" data-testid="contact-support-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-medium text-primary-900">Contact & Support</h3>
              </div>
              <div className="space-y-3">
                <a href="mailto:michaela@playfirstapp.com" className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors">
                  <Mail className="w-4 h-4 text-stone-500" />
                  <span className="text-sm text-stone-700">michaela@playfirstapp.com</span>
                </a>
                <a href="https://instagram.com/playfirstapp" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors">
                  <ExternalLink className="w-4 h-4 text-stone-500" />
                  <span className="text-sm text-stone-700">@playfirstapp on Instagram</span>
                </a>
              </div>
            </Card>

          </div>
        </motion.div>
      </main>

      {/* Upgrade Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="rounded-3xl max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="upgrade-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif">Choose Your Plan</DialogTitle>
          </DialogHeader>

          {/* Monthly / Annual billing toggle */}
          <div className="flex items-center justify-center mt-4">
            <div
              className="inline-flex items-center gap-1 p-1 rounded-full bg-stone-100 border border-stone-200"
              data-testid="upgrade-billing-toggle"
            >
              <button
                type="button"
                onClick={() => setUpgradeBillingPeriod('monthly')}
                data-testid="upgrade-billing-monthly-btn"
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  upgradeBillingPeriod === 'monthly'
                    ? 'bg-white text-primary-900 shadow-sm'
                    : 'text-stone-500'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setUpgradeBillingPeriod('annual')}
                data-testid="upgrade-billing-annual-btn"
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                  upgradeBillingPeriod === 'annual'
                    ? 'bg-white text-primary-900 shadow-sm'
                    : 'text-stone-500'
                }`}
              >
                Annual
                <span className="text-[10px] bg-emerald-100 text-emerald-700 font-semibold px-1.5 py-0.5 rounded">
                  Save ~25%
                </span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            {Object.entries(PLANS)
              .filter(([key, plan]) => ['free', 'basic', 'pro', 'clinical'].includes(key) && !plan.hidden)
              .map(([key, plan]) => (
              <div
                key={key}
                className={`relative p-6 rounded-2xl border-2 transition-all ${
                  currentPlan === key 
                    ? 'border-primary bg-primary/5' 
                    : 'border-stone-200 hover:border-stone-300'
                } ${key === 'clinical' ? 'md:col-span-1' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-white text-xs px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                
                {key === 'clinical' && (
                  <div className="absolute -top-3 right-4">
                    <span className="bg-purple-600 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1">
                      <Building2 className="w-3 h-3" />
                      Practice
                    </span>
                  </div>
                )}
                
                <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-3 ${plan.color}`}>
                  {plan.name}
                </div>
                
                {(() => {
                  // Compute period-aware display price for paid plans.
                  // Annual = monthly × 12 × 0.75 (~25% discount).
                  const paidKeys = ['basic', 'pro', 'clinical'];
                  if (!paidKeys.includes(key)) {
                    return (
                      <>
                        <p className="text-2xl font-bold text-primary-900 mb-1">{plan.price}</p>
                        <p className="text-xs text-stone-500 mb-3">
                          Billed in ZAR. Final amount may vary slightly based on exchange rates.
                        </p>
                      </>
                    );
                  }
                  const monthly = plan.priceValue || 0;
                  if (upgradeBillingPeriod === 'annual') {
                    const annual = Math.round(monthly * 12 * 0.75 * 100) / 100;
                    return (
                      <>
                        <p className="text-2xl font-bold text-primary-900 mb-0.5">
                          ${annual}<span className="text-sm font-normal text-stone-500">/year</span>
                        </p>
                        <p className="text-xs text-emerald-700 font-medium mb-1">
                          Save ${Math.round((monthly * 12 - annual) * 100) / 100} vs monthly
                        </p>
                        <p className="text-xs text-stone-500 mb-3">
                          Billed in ZAR. Final amount may vary slightly based on exchange rates.
                        </p>
                      </>
                    );
                  }
                  return (
                    <>
                      <p className="text-2xl font-bold text-primary-900 mb-1">{plan.price}</p>
                      <p className="text-xs text-stone-500 mb-3">
                        Billed in ZAR. Final amount may vary slightly based on exchange rates.
                      </p>
                    </>
                  );
                })()}
                
                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                {key === 'clinical' && (
                  <div className="mb-4 p-3 bg-purple-50 rounded-xl border border-purple-100">
                    <p className="text-xs text-purple-700 flex items-start gap-2">
                      <ScrollText className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      Requires acceptance of Clinical License Terms
                    </p>
                  </div>
                )}
                
                <Button
                  className={`w-full rounded-full ${currentPlan === key ? 'bg-stone-200 text-stone-600' : ''} ${key === 'clinical' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                  variant={currentPlan === key ? 'outline' : 'default'}
                  disabled={currentPlan === key}
                  onClick={() => handlePlanSelect(key)}
                  data-testid={`select-${key}-plan`}
                >
                  {currentPlan === key ? 'Current Plan' : key === 'free' ? 'Activate Free Plan' : 'Subscribe'}
                </Button>
              </div>
            ))}
          </div>
          
          {/* Discount Code Section */}
          <div className="mt-6 p-4 bg-green-50 rounded-2xl border border-green-200">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="w-4 h-4 text-green-600" />
              <p className="text-sm font-medium text-green-800">Have a discount code?</p>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Enter code"
                value={discountCode}
                onChange={(e) => {
                  setDiscountCode(e.target.value.toUpperCase());
                  setDiscountError('');
                  setDiscountValid(false);
                }}
                className="flex-1 rounded-full uppercase"
                data-testid="discount-code-input"
              />
              <Button
                variant="outline"
                className="rounded-full"
                onClick={validateDiscountCode}
                disabled={!discountCode.trim()}
              >
                Apply
              </Button>
            </div>
            {discountError && (
              <div className="mt-2 p-2 bg-red-100 rounded-xl">
                <p className="text-sm text-red-800">{discountError}</p>
              </div>
            )}
            {discountValid && (
              <div className="mt-2 p-2 bg-green-100 rounded-xl">
                <p className="text-sm text-green-800">
                  ✓ Code applied! Discount will be reflected in your subscription.
                </p>
              </div>
            )}
          </div>
          
          <p className="text-center text-sm text-stone-500 mt-4">
            Prices shown in USD. Billed monthly via Paystack in ZAR.
          </p>
        </DialogContent>
      </Dialog>

      {/* Clinical Plan Terms Dialog */}
      <Dialog open={showClinicalTerms} onOpenChange={setShowClinicalTerms}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="clinical-terms-dialog">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-serif">Clinical License Terms</DialogTitle>
                <p className="text-sm text-stone-500">Practice & Organization License Agreement</p>
              </div>
            </div>
          </DialogHeader>
          
          <div className="mt-4 space-y-4">
            {/* Summary Box */}
            <div className="p-4 bg-purple-50 rounded-2xl border border-purple-200">
              <h4 className="font-medium text-purple-900 mb-2">Clinical Plan Summary</h4>
              <ul className="space-y-1 text-sm text-purple-700">
                <li>• Base price: {PLANS.clinical.price} (includes 5 therapist seats)</li>
                <li>• Additional therapists: $9.99/month each</li>
                <li>• Unlimited children, AI reports & activities</li>
                <li>• All therapists must be from the same organization</li>
                <li>• Billed in ZAR via Paystack</li>
              </ul>
            </div>

            {/* Terms Content */}
            <div className="bg-stone-50 rounded-2xl p-4 max-h-64 overflow-y-auto">
              <pre className="text-xs text-stone-600 whitespace-pre-wrap font-sans leading-relaxed">
                {CLINICAL_TERMS}
              </pre>
            </div>

            {/* Acceptance Checkbox */}
            <div className="flex items-start space-x-3 p-4 bg-amber-50 rounded-2xl border border-amber-200">
              <Checkbox
                id="clinical-terms-checkbox"
                data-testid="clinical-terms-checkbox"
                checked={clinicalTermsAccepted}
                onCheckedChange={setClinicalTermsAccepted}
                className="mt-0.5"
              />
              <label
                htmlFor="clinical-terms-checkbox"
                className="text-sm text-amber-800 cursor-pointer leading-relaxed"
              >
                I confirm that I am authorized to enter this agreement on behalf of my practice/organization, and I agree to the Clinical License Terms of Use. I understand that this license is restricted to therapists employed within the same registered practice.
              </label>
            </div>
          </div>

          <DialogFooter className="mt-6 flex gap-3">
            <Button
              variant="outline"
              className="flex-1 rounded-full"
              onClick={() => setShowClinicalTerms(false)}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 rounded-full bg-purple-600 hover:bg-purple-700"
              data-testid="accept-clinical-terms"
              onClick={handleClinicalTermsAccept}
              disabled={!clinicalTermsAccepted}
            >
              Accept & Subscribe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Join Practice Dialog */}
      <Dialog open={showJoinPracticeDialog} onOpenChange={setShowJoinPracticeDialog}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif text-center">Join a Practice</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-stone-600 text-center">
              Enter the invite code provided by your practice administrator to join their team.
            </p>
            
            <div className="space-y-2">
              <Label htmlFor="join-code">Invite Code</Label>
              <Input
                id="join-code"
                placeholder="Enter code (e.g., ABC123)"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                className="h-12 rounded-xl text-center text-lg tracking-widest font-mono"
                data-testid="join-practice-code-input"
              />
            </div>
            
            <div className="bg-indigo-50 rounded-xl p-3 text-sm text-indigo-700">
              <p className="font-medium mb-1">What happens when you join?</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Your plan will be upgraded to Clinical</li>
                <li>You'll have access to your practice's shared resources</li>
                <li>Your practice admin can view practice-wide analytics</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 rounded-full"
              onClick={() => {
                setShowJoinPracticeDialog(false);
                setJoinCode('');
              }}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 rounded-full bg-indigo-600 hover:bg-indigo-700"
              onClick={handleJoinPractice}
              disabled={joiningPractice || !joinCode.trim()}
              data-testid="submit-join-practice"
            >
              {joiningPractice ? 'Joining...' : 'Join Practice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Subscription Dialog */}
      <Dialog open={showManageSubscription} onOpenChange={setShowManageSubscription}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif text-center">Manage Subscription</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-stone-50 rounded-xl">
              <div className="flex justify-between items-center">
                <span className="text-stone-600">Current Plan</span>
                <span className="font-medium text-primary-900">{currentPlanData?.name || 'Free Trial'}</span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-stone-600">Status</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  subscriptionDetails?.subscription_status === 'active' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-stone-100 text-stone-600'
                }`}>
                  {subscriptionDetails?.subscription_status || 'Inactive'}
                </span>
              </div>
              {subscriptionDetails?.subscription_started && (
                <div className="flex justify-between items-center mt-2">
                  <span className="text-stone-600">Started</span>
                  <span className="text-stone-700">
                    {new Date(subscriptionDetails.subscription_started).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>

            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> If you cancel, you'll retain access until the end of your current billing period. 
                You can re-subscribe anytime.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <Button
                variant="outline"
                className="w-full rounded-full"
                onClick={() => {
                  setShowManageSubscription(false);
                  setShowUpgradeDialog(true);
                }}
              >
                <Zap className="w-4 h-4 mr-2" />
                Change Plan
              </Button>
              <Button
                variant="outline"
                className="w-full rounded-full border-red-200 text-red-600 hover:bg-red-50"
                onClick={handleCancelSubscription}
                disabled={cancellingSubscription}
                data-testid="confirm-cancel-subscription"
              >
                {cancellingSubscription ? 'Cancelling...' : 'Cancel Subscription'}
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className="w-full rounded-full"
              onClick={() => setShowManageSubscription(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
